/**
 * @file Жизненный цикл обновления баланса на странице хранилища.
 *
 * Не запускает два чтения одновременно, сохраняет последний успешный ответ
 * и планирует очередное обновление через 60 секунд после завершения запроса.
 */
import { useEffect, useRef, useState } from 'react'

import { rememberQuota } from '../transfers/snapshots'
import { fetchTrafficQuota } from './api'
import type { TrafficQuota } from './quota'

interface QuotaState {
    boxId?: string
    data: TrafficQuota | null
    updatedAt: Date | null
    pending: boolean
    error: boolean
}

const initialState: QuotaState = {
    data: null,
    updatedAt: null,
    pending: true,
    error: false,
}

export function useTrafficQuota({
    boxId,
    connected,
}: {
    boxId?: string
    connected: boolean
}) {
    const [state, setState] = useState<QuotaState>(initialState)
    const refreshRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        if (!boxId || !connected) return

        const controller = new AbortController()
        let timer: ReturnType<typeof setTimeout> | undefined
        let pending = false

        async function refresh() {
            // Таймер, ручное нажатие и завершение передачи могут совпасть.
            // Уже выполняющееся чтение повторно не запускаем.
            if (pending || controller.signal.aborted) return

            pending = true
            clearTimeout(timer)

            // Старые числа сохраняем на время обновления; при смене хранилища
            // начинаем с пустого состояния, чтобы не показать чужой баланс.
            setState((previous) => ({
                ...(previous.boxId === boxId ? previous : initialState),
                boxId,
                pending: true,
                error: false,
            }))

            const signal = AbortSignal.any([
                controller.signal,
                AbortSignal.timeout(30_000),
            ])

            try {
                const data = await fetchTrafficQuota(boxId!, signal)
                if (!controller.signal.aborted) {
                    // Передачи читают тот же успешный снимок, что получает экран.
                    rememberQuota(boxId!, data)
                    setState({
                        boxId,
                        data,
                        updatedAt: new Date(),
                        pending: false,
                        error: false,
                    })
                }
            } catch {
                if (!controller.signal.aborted) {
                    setState((previous) => ({
                        ...previous,
                        pending: false,
                        error: true,
                    }))
                }
            } finally {
                // Следующий опрос планируется после окончания предыдущего,
                // а не по независимому интервалу: медленные запросы не наложатся.
                pending = false
                if (!controller.signal.aborted)
                    timer = setTimeout(() => void refresh(), 60_000)
            }
        }

        // Внешняя кнопка получает доступ к refresh через ref. Сам refresh
        // замкнут на controller только текущего подключения.
        refreshRef.current = () => void refresh()
        void refresh()

        // Уход со страницы отменяет чтение и будущий таймер вместе.
        return () => {
            controller.abort()
            clearTimeout(timer)
            refreshRef.current = null
        }
    }, [boxId, connected])

    const current = state.boxId === boxId ? state : initialState

    return {
        ...current,
        pending: connected && current.pending,
        refresh: () => refreshRef.current?.(),
    }
}
